import { ClientResponseError } from "pocketbase";
import { planFeatureOf } from "@/lib/plans/plan-errors";
import { translatedCode } from "./server-errors";

type FieldError = { code?: string; message?: string; params?: object };

export type SetFieldError = (name: string, error: { type: string; message: string }) => void;

/**
 * Maps a PocketBase validation error onto form fields.
 *
 * PocketBase returns `{ data: { <field>: { code, message } } }` for 400 responses. Every field
 * that exists on the form gets an inline error; the returned string is what is left to show
 * globally (a toast), or `null` if everything was attached to a field. Plan refusals return
 * `null` too: the upgrade dialog explains them (App.tsx).
 */
export function applyServerErrors(
  error: unknown,
  setError: SetFieldError,
  fields?: readonly string[],
): string | null {
  if (!(error instanceof ClientResponseError)) {
    return error instanceof Error ? error.message : "Something went wrong";
  }
  if (planFeatureOf(error)) return null;
  if (error.status === 429) {
    const codes = Object.values((error.response?.data ?? {}) as Record<string, FieldError>);
    return (
      codes.map((e) => translatedCode(e?.code)).find(Boolean) ?? translatedCode("rate_limited")
    );
  }

  const data = (error.response?.data ?? {}) as Record<string, FieldError>;
  let attached = 0;
  let unattached: string | null = null;
  for (const [name, fieldError] of Object.entries(data)) {
    const message = translatedCode(fieldError?.code, fieldError?.params) ?? fieldError?.message;
    if (fields && !fields.includes(name)) {
      unattached ??= translatedCode(fieldError?.code, fieldError?.params);
      continue;
    }
    setError(name, { type: "server", message: message || "Invalid value" });
    attached += 1;
  }

  if (attached > 0) return null;
  return unattached ?? (error.response?.message || error.message || "Request failed");
}

/** True for PocketBase 404s, which also cover records hidden by API rules. */
export function isNotFound(error: unknown): boolean {
  return error instanceof ClientResponseError && error.status === 404;
}
